/**
 * Underlying readout for the iron-condor card: last price, change on the
 * day, and how far the price sits from each short strike.
 *
 * Two drivers, one presentation (`UnderlyingReadout`):
 *
 *   - LIVE (`UnderlyingQuote`): the existing quote feed — /api/trade/quote
 *     snapshot (carries previousClose, so the day's change) merged with
 *     live /ws/quotes ticks (useQuoteStream, the same hook the Trade page
 *     uses). When neither has a price (feed down, market data not
 *     configured) it falls back to the last position mark's refLast,
 *     labelled with its time, so the card never shows a stale number as
 *     if it were live.
 *
 *   - REPLAY (`UnderlyingReplay`): NO quote stream. Driven by the replayed
 *     position mark at the playhead — its refLast is the price, its time
 *     is the label. The change is vs that day's PREVIOUS CLOSE when the
 *     server sends it (/marks dayCondor.prevClose, server PR #131: the
 *     prior session's split-adjusted close); otherwise vs the day's first
 *     mark, and the hover says which.
 *
 * Read-only: no controls, nothing here writes.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useQuoteStream } from '../../hooks/useQuoteStream';

/** The cycle's strikes (/marks dayCondor, from the day's SETUP event). */
export interface DayCondor {
  putBuyStrike?: number | null;
  putSellStrike?: number | null;
  callSellStrike?: number | null;
  callBuyStrike?: number | null;
  contracts?: number | null;
  netCredit?: number | null;
  /** The underlying's prior-session close for that day (null = unknown). */
  prevClose?: number | null;
}

const MONO = 'monospace';
const fmt2 = (v: number) => v.toFixed(2);
const signed = (v: number, d = 2) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(d)}`;

interface ReadoutProps {
  symbol: string;
  price: number | null;
  /** Reference price the change is measured from (prev close / first mark). */
  reference: number | null;
  /** What the change is measured against, for the hover title. */
  referenceLabel?: string;
  /** Source caption after the symbol ("live", "snapshot", "replay · mark 12:40 ET"). */
  sourceLabel: string;
  /** Green dot = ticks are arriving. */
  streaming?: boolean;
  condor?: DayCondor | null;
  /** Shown when there is no price. */
  emptyLabel?: string;
}

/** Presentation only — no data fetching. */
export const UnderlyingReadout: React.FC<ReadoutProps> = ({
  symbol, price, reference, referenceLabel, sourceLabel, streaming = false, condor, emptyLabel,
}) => {
  const change = price != null && reference ? price - reference : null;
  const changePct = change != null && reference ? (100 * change) / reference : null;

  const ps = condor?.putSellStrike ?? null;
  const cs = condor?.callSellStrike ?? null;
  const pw = condor?.putBuyStrike ?? null;
  const cw = condor?.callBuyStrike ?? null;
  // Cushion > 0 = price still inside that short strike.
  const putGap = price != null && ps != null ? price - ps : null;
  const callGap = price != null && cs != null ? cs - price : null;

  if (price == null) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
        {symbol} — {emptyLabel || 'waiting for a quote…'}
      </Typography>
    );
  }

  const gapCell = (label: string, gap: number | null) => {
    if (gap == null) return null;
    const pct = (100 * gap) / price;
    const breached = gap <= 0;
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: 10.5, color: 'text.disabled', letterSpacing: '.04em' }}>
          {label}
        </Typography>
        <Typography
          component="div"
          sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600,
                color: breached ? 'error.main' : 'text.primary', whiteSpace: 'nowrap' }}
        >
          {breached ? 'ITM ' : ''}{fmt2(Math.abs(gap))}
          <Box component="span" sx={{ fontWeight: 400, color: breached ? 'error.main' : 'text.secondary' }}>
            {' '}({Math.abs(pct).toFixed(2)}%)
          </Box>
        </Typography>
      </Box>
    );
  };

  // Position bar: wing-to-wing, short strikes marked, price as a dot.
  let bar: React.ReactNode = null;
  if (ps != null && cs != null) {
    const lo = pw ?? ps - (cs - ps) * 0.25;
    const hi = cw ?? cs + (cs - ps) * 0.25;
    const span = hi - lo;
    const at = (v: number) => `${Math.min(100, Math.max(0, (100 * (v - lo)) / span))}%`;
    const inside = price > ps && price < cs;
    bar = (
      <Tooltip title={`${symbol} ${fmt2(price)} · shorts ${ps}P / ${cs}C${pw != null && cw != null ? ` · wings ${pw} / ${cw}` : ''}`}>
        <Box sx={{ position: 'relative', height: 14, mt: 0.75, mx: 0.5 }}>
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: 6, height: 2, bgcolor: 'action.hover', borderRadius: 1 }} />
          <Box sx={{ position: 'absolute', left: at(ps), width: `calc(${at(cs)} - ${at(ps)})`, top: 6, height: 2,
                     bgcolor: 'success.main', opacity: 0.35 }} />
          {[ps, cs].map((k) => (
            <Box key={k} sx={{ position: 'absolute', left: at(k), top: 2, width: 2, height: 10, ml: '-1px',
                               bgcolor: 'text.secondary', borderRadius: 1 }} />
          ))}
          <Box sx={{ position: 'absolute', left: at(price), top: 3, width: 8, height: 8, ml: '-4px', borderRadius: '50%',
                     bgcolor: inside ? 'primary.main' : 'error.main',
                     boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}`,
                     transition: 'left 0.4s ease' }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{ mt: 1, px: 1.25, py: 0.75, border: 1, borderColor: 'divider', borderRadius: 1.5 }}
      aria-label={`${symbol} price`}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: 10.5, color: 'text.disabled', letterSpacing: '.04em',
                                             display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%',
              bgcolor: streaming ? 'success.main' : 'text.disabled' }} />
            {symbol} · {sourceLabel}
          </Typography>
          <Tooltip title={change != null && referenceLabel ? `change vs ${referenceLabel} (${fmt2(reference!)})` : ''}>
            <Typography component="div" sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {fmt2(price)}
              {change != null && changePct != null && (
                <Box component="span" sx={{ ml: 1, fontSize: 12.5, fontWeight: 500,
                                            color: change >= 0 ? 'success.main' : 'error.main' }}>
                  {signed(change)} ({signed(changePct)}%)
                </Box>
              )}
            </Typography>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
          {gapCell(ps != null ? `to ${ps}P short` : 'put short', putGap)}
          {gapCell(cs != null ? `to ${cs}C short` : 'call short', callGap)}
        </Box>
      </Box>
      {bar}
    </Box>
  );
};

interface LiveProps {
  symbol: string;
  condor?: DayCondor | null;
  /** Last mark's underlying price + its ET stamp, as a fallback. */
  fallback?: { price: number; at: string } | null;
}

/** LIVE driver: the quote feed, with the last mark as a labelled fallback. */
const UnderlyingQuote: React.FC<LiveProps> = ({ symbol, condor, fallback }) => {
  const { quote, status } = useQuoteStream(symbol);
  const livePrice = quote?.price ?? quote?.last ?? null;
  const price = livePrice ?? fallback?.price ?? null;
  const fromMark = livePrice == null && fallback != null;
  const streaming = status === 'open' && quote?.tradeTimestamp != null;
  const sourceLabel = fromMark
    ? `last mark ${fallback!.at.slice(11, 16)} ET`
    : streaming ? 'live' : 'snapshot';
  return (
    <UnderlyingReadout
      symbol={symbol}
      price={price}
      reference={quote?.previousClose ?? null}
      referenceLabel="previous close"
      sourceLabel={sourceLabel}
      streaming={streaming}
      condor={condor}
    />
  );
};

interface ReplayProps {
  symbol: string;
  condor?: DayCondor | null;
  /** The replayed mark at the playhead (null = no position yet then). */
  head: { price: number | null; at: string } | null;
  /** The day's first mark with a price — the change's reference. */
  first: { price: number; at: string } | null;
  /** Playhead label, e.g. "12:40" (null = full day). */
  headLabel: string | null;
}

/** REPLAY driver: the mark at the playhead; never touches the quote feed. */
export const UnderlyingReplay: React.FC<ReplayProps> = ({ symbol, condor, head, first, headLabel }) => (
  <UnderlyingReadout
    symbol={symbol}
    price={head?.price ?? null}
    reference={condor?.prevClose ?? first?.price ?? null}
    referenceLabel={condor?.prevClose != null
      ? 'prev close'
      : first ? `first mark ${first.at.slice(11, 16)} ET` : undefined}
    sourceLabel={head ? `replay · mark ${head.at.slice(11, 16)} ET` : 'replay'}
    streaming={false}
    condor={condor}
    emptyLabel={headLabel ? `no mark yet at ${headLabel}` : 'no marks this day'}
  />
);

export default UnderlyingQuote;
